import React from 'react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  CheckCircle,
  AlertTriangle,
  Clock,
  User,
  Building2,
  Calendar,
  ArrowRight,
  X
} from 'lucide-react';

interface NotificationToastProviderProps {
  children: React.ReactNode;
}

const statusChangeMessages = {
  PendingVendorContact: 'Contacting vendors for availability',
  VendorNegotiating: 'Vendor is responding with proposals',
  PendingOrganizationApproval: 'Awaiting organization approval',
  ApprovedAwaitingVendorBooking: 'Approved, finalizing with vendor',
  Confirmed: 'Booking confirmed and scheduled',
  Cancelled: 'Booking has been cancelled',
  NoVendorsAvailable: 'No vendors available for this request'
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'Confirmed':
      return CheckCircle;
    case 'Cancelled':
    case 'NoVendorsAvailable':
      return AlertTriangle;
    case 'PendingOrganizationApproval':
      return User;
    case 'ApprovedAwaitingVendorBooking':
      return Calendar;
    default:
      return Clock;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Confirmed':
      return 'text-green-600';
    case 'Cancelled':
    case 'NoVendorsAvailable':
      return 'text-red-600';
    case 'PendingOrganizationApproval':
      return 'text-purple-600';
    case 'ApprovedAwaitingVendorBooking':
      return 'text-orange-600';
    default:
      return 'text-blue-600';
  }
};

export function NotificationToastProvider({
  children
}: NotificationToastProviderProps) {
  // Simplified provider without polling - just passes children through
  // Toast functionality is available via useNotificationToast hook
  return <>{children}</>;
}

// Hook for manual notification triggering
export function useNotificationToast() {
  const { toast } = useToast();

  const showStatusChangeToast = React.useCallback((
    bookingId: string,
    fromStatus: string,
    toStatus: string,
    title?: string
  ) => {
    const StatusIcon = getStatusIcon(toStatus);
    const statusColor = getStatusColor(toStatus);

    toast({
      title: (
        <div className="flex items-center gap-2">
          <StatusIcon className={cn('w-4 h-4', statusColor)} />
          Booking Status Updated
        </div>
      ),
      description: (
        <div className="space-y-2">
          <p>{title || `Booking ${bookingId}`}</p>
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="outline" className="text-xs">
              {fromStatus}
            </Badge>
            <ArrowRight className="w-3 h-3" />
            <Badge variant="outline" className="text-xs">
              {toStatus}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {statusChangeMessages[toStatus as keyof typeof statusChangeMessages]}
          </p>
        </div>
      ),
      duration: 5000,
      action: (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (typeof window !== 'undefined') {
              window.location.href = `/bookings/${bookingId}`;
            }
          }}
        >
          View
        </Button>
      ),
    });
  }, [toast]);

  const showSuccessToast = React.useCallback((
    title: string,
    message: string,
    bookingId?: string
  ) => {
    toast({
      title: (
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600" />
          {title}
        </div>
      ),
      description: message,
      duration: 4000,
      action: bookingId ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (typeof window !== 'undefined') {
              window.location.href = `/bookings/${bookingId}`;
            }
          }}
        >
          View
        </Button>
      ) : undefined,
    });
  }, [toast]);

  const showErrorToast = React.useCallback((
    title: string,
    message: string
  ) => {
    toast({
      title: (
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {title}
        </div>
      ),
      description: message,
      variant: 'destructive',
      duration: 6000,
    });
  }, [toast]);

  return {
    showStatusChangeToast,
    showSuccessToast,
    showErrorToast
  };
}